import React, { ReactElement, useContext } from 'react';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { GameState } from './Room';
import { UserContext } from '../contexts/UserContext';
import { UserStatus } from '../api/models/User';
import { Category, Action } from '../analytics/Tracking';
import { useTracking } from '../analytics/useTracking';

interface ModerationPanelProps {
    gameState: GameState;
    isRevealEnabled: boolean;
    onStarted?: () => void;
    onStopped?: () => void;
    onReEstimate?: () => void;
    showReEstimate?: boolean; // Whether to show the re-estimate button
}

export default function ModerationPanel(props: ModerationPanelProps): ReactElement {
 
    const { user } = useContext(UserContext);
    const { track } = useTracking();

    function handleButtonClick() {
        if (props.gameState === GameState.WaitingForPLayers) {
            if (props.onStarted != null) {
                props.onStarted();
            }
        } else if (props.gameState === GameState.Playing) {
            if (props.onStopped) {
                props.onStopped();
            }
        } else if (props.gameState === GameState.Result) {
            if (props.onStarted != null) {
                props.onStarted();
            }
        }
    }

    function handleReEstimateClick() {
        track.event(Category.Game, Action.ReEstimateClicked);
        if (props.onReEstimate != null) {
            props.onReEstimate();
        }
    }

    function buttonTitle(gameState: GameState) {
        switch (gameState) {
            case GameState.WaitingForPLayers:
                return "Start Estimating"
            case GameState.Playing:
                return "Reveal Estimates"
            case GameState.Result:
                return "Start New Estimation Round"
            default:
                return "?"
        }
    }

    const isDisabled = user?.status == UserStatus.Suspended || (props.gameState === GameState.Playing && !props.isRevealEnabled);

    function getTooltip(gameState: GameState): string {
        switch (gameState) {
            case GameState.WaitingForPLayers:
                return "Begin the estimation round for all players";
            case GameState.Playing:
                return "Show all players' estimates";
            case GameState.Result:
                return "Start a completely new estimation round for a new story or task";
            default:
                return "";
        }
    }

    const renderButton = (onClick: () => void, title: string, tooltipText: string, color: "primary" | "secondary", marginBottom?: number) => (
        <Grid>
            <Tooltip title={tooltipText} enterDelay={1500}>
                <span>
                    <Button 
                        disabled={isDisabled} 
                        onClick={onClick} 
                        variant="contained" 
                        size="large" 
                        color={color}
                        sx={marginBottom !== undefined ? { mb: marginBottom } : {}}
                    >
                        {title}
                    </Button>
                </span>
            </Tooltip>
        </Grid>
    );

    return (
        <Grid container spacing={2} direction="column" justifyContent="space-between" alignItems="center">
            {props.gameState === GameState.Result ? (
                <>
                    {renderButton(
                        handleButtonClick,
                        buttonTitle(props.gameState),
                        getTooltip(GameState.Result),
                        "secondary",
                        props.showReEstimate ? 0.5 : 0
                    )}
                    {props.showReEstimate && renderButton(
                        handleReEstimateClick,
                        "Re-estimate",
                        "Re-estimate the current story to improve team alignment",
                        "primary"
                    )}
                </>
            ) : (
                renderButton(
                    handleButtonClick,
                    buttonTitle(props.gameState),
                    getTooltip(props.gameState),
                    "secondary"
                )
            )}
        </Grid>
    );

}
